<template>
  <!--
 TagCellElementLite, multi-root.
 Когда tag-status (DestType=Tag), рендерим текст + edit-button (display-buttons-on-hover).
 Edit-кнопка открывает popover на @floating-ui, реализован через MostaContextMenu.
    Inline-редактор статуса = MostaSelect.
  -->
  <span
    v-for="(rec, i) in rows"
    :key="'tag_' + i"
    class="qtl-tag-row"
    :style="{ color: rec.color }"
  >{{ rec.text }}</span>

  <button
    v-if="isTagNameStatus"
    ref="editBtnRef"
    class="qtl-tag-edit-btn"
    type="button"
    :aria-label="t('edit_label')"
    @click.stop="openEditMenu"
  >
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
    </svg>
  </button>

  <mosta-context-menu
    v-if="isTagNameStatus"
    :visible="menuOpen"
    :position="menuPos"
    @close="menuOpen = false"
  >
    <div class="qtl-tag-edit-popover">
      <mosta-select
        class="qtl-tag-edit-popover__select"
        :model-value="tempStatus"
        :options="tagStatusOptions"
        :label="t('status_label')"
        @update:modelValue="(v: any) => { tempStatus = v }"
      />
      <button
        type="button"
        class="qtl-tag-edit-popover__apply"
        :aria-label="t('apply_changes')"
        @click="applyChanges"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
        </svg>
      </button>
    </div>
  </mosta-context-menu>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import {
  AnalyticsV2ElementType,
  ColumnCategory,
  TableDataCellDisplayType,
  TagType,
} from "@/models/etc/types";
import type {
  CellDataTagStatus,
  TableDataCell,
} from "@/models/interfaces/entity-settings/querytable/querytable";
import { auxStore } from "@/store/auxillary";
import { generalStore } from "@/store/general";
import { i18n } from "@/i18n";
import MostaContextMenu from "@/components/common/basic/MostaContextMenu.vue";
import MostaSelect from "@/components/common/MostaSelect.vue";

const props = defineProps<{
  element: TableDataCell;
  entityId?: number;
}>();

const emit = defineEmits<{
  (e: "refreshNeeded"): void;
}>();

const t = (k: string) => i18n.global.t(k);

const aux = auxStore();
const gstore = generalStore();

interface TagRow { text: string; color: string; }

function tagStatusName(arg: number): string {
  switch (arg) {
    case TagType.Standard: return t("normal_label");
    case TagType.Hidden: return t("hidden_upcase");
    default: return t("status_unknown");
  }
}

const rows = computed<TagRow[]>(() => {
  return (props.element.Data as CellDataTagStatus[]).map((cell) => {
    switch (cell.Status) {
      case TagType.Hidden:
        return { text: tagStatusName(cell.Status), color: "blue" };
      case TagType.Standard:
      default:
        return { text: tagStatusName(cell.Status), color: "black" };
    }
  });
});

const isTagNameStatus = computed<boolean>(() =>
  props.element.Category === ColumnCategory.Status &&
  props.element.DisplayType === TableDataCellDisplayType.TagStatus &&
  gstore.selectedItem?.DestType === AnalyticsV2ElementType.Tag,
);

const tagStatusOptions = computed(() =>
  Object.values(TagType)
    .filter((v) => !isNaN(Number(v)) && Number(v) >= 0)
    .map((vm) => ({ label: tagStatusName(Number(vm)), value: Number(vm) })),
);

const initialStatus = props.element.Data?.[0]
  ? (props.element.Data[0] as CellDataTagStatus).Status
  : TagType.Standard;
const tempStatus = ref<{ label: string; value: number } | null>(
  { label: tagStatusName(initialStatus), value: initialStatus },
);

const menuOpen = ref(false);
const menuPos = ref<{ top: string; left: string }>({ top: "0px", left: "0px" });
const editBtnRef = ref<HTMLElement | null>(null);

function openEditMenu(ev: MouseEvent) {
  // Позиционируем popover у нижнего-правого угла кнопки (как было в q-menu anchor="bottom end").
  const rect = editBtnRef.value?.getBoundingClientRect();
  if (rect) {
    menuPos.value = { top: `${rect.bottom + 2}px`, left: `${rect.right - 200}px` };
  } else {
    menuPos.value = { top: `${ev.clientY}px`, left: `${ev.clientX}px` };
  }
  menuOpen.value = true;
}

async function applyChanges() {
  if (!aux || !props.entityId || !tempStatus.value) return;
  if (!isTagNameStatus.value) return;
  const ok = await aux.updateSingleTagStatus({
    TagId: props.entityId,
    NewStatus: tempStatus.value.value,
  });
  if (ok) {
    menuOpen.value = false;
    emit("refreshNeeded");
  }
}
</script>

<style lang="scss" scoped>
.qtl-tag-row {
  display: block;
  max-width: 100%;
  margin-right: 0;
  font-size: 13px;
  overflow-wrap: anywhere;
  word-break: normal;
  line-height: 1.2;
}

.qtl-tag-edit-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #888;
  padding: 2px 4px;
  border-radius: 3px;
  /* Скрываем по умолчанию, показываем при hover на родительском td (display-buttons-on-hover-эквивалент). */
  opacity: 0;
}

.qtl-tag-edit-btn:hover,
.qtl-tag-edit-btn:focus,
:deep(.qtl-row:hover) .qtl-tag-edit-btn {
  opacity: 1;
}

.qtl-tag-edit-popover {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.qtl-tag-edit-popover__select {
  min-width: 150px;
}

.qtl-tag-edit-popover__apply {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #333;
  padding: 4px;
}

.qtl-tag-edit-popover__apply:hover {
  color: #333333;
}
</style>
